<template>
    <div class="nch--wiki-editor">
        <div v-if="editingBy" class="alert alert-warning py-2 mb-3"><i class="fas fa-pen mr-1"></i>{{ $t('wiki.editor.being_edited', { user: editingBy }) }}</div>

        <div v-if="conflict" class="alert alert-danger mb-3">
            <strong>{{ $t('wiki.editor.conflict_title') }}</strong>
            <div class="small">{{ $t('wiki.editor.conflict_text') }}</div>
            <div class="nch--diff-cols mt-2">
                <div class="nch--diff-col">
                    <div class="small font-weight-bold mb-1">{{ $t('wiki.editor.conflict_mine') }}</div>
                    <pre class="nch--diff-pre"><template v-for="(op, i) in conflict.ops" :key="'m' + i"><span v-if="op.type === 'equal'">{{ op.text }}</span><ins v-else-if="op.type === 'insert'" class="nch--diff-ins">+{{ op.text }}</ins></template></pre>
                </div>
                <div class="nch--diff-col">
                    <div class="small font-weight-bold mb-1">{{ $t('wiki.editor.conflict_theirs') }} ({{ conflict.user || '—' }})</div>
                    <pre class="nch--diff-pre"><template v-for="(op, i) in conflict.ops" :key="'t' + i"><span v-if="op.type === 'equal'">{{ op.text }}</span><del v-else-if="op.type === 'delete'" class="nch--diff-del">−{{ op.text }}</del></template></pre>
                </div>
            </div>
            <div class="mt-2">
                <button type="button" class="btn btn-sm btn-outline-secondary mr-2" @click="useCurrent">{{ $t('wiki.editor.use_current') }}</button>
                <button type="button" class="btn btn-sm btn-outline-primary" @click="keepMine">{{ $t('wiki.editor.keep_mine') }}</button>
            </div>
        </div>

        <div class="nch--wiki-toolbar btn-toolbar mb-2" role="toolbar">
            <div class="btn-group btn-group-sm mr-2 mb-1">
                <button type="button" class="btn btn-light" :title="$t('wiki.editor.bold')" @click="format('bold')"><i class="fas fa-bold"></i></button>
                <button type="button" class="btn btn-light" :title="$t('wiki.editor.italic')" @click="format('italic')"><i class="fas fa-italic"></i></button>
                <button type="button" class="btn btn-light" :title="$t('wiki.editor.heading')" @click="heading"><i class="fas fa-heading"></i></button>
                <button type="button" class="btn btn-light" :title="$t('wiki.editor.list')" @click="format('list', 'bullet')"><i class="fas fa-list-ul"></i></button>
            </div>
            <div class="btn-group btn-group-sm mb-1">
                <button type="button" class="btn btn-light" :title="$t('wiki.editor.table')" @click="insertTable"><i class="fas fa-table"></i></button>
                <button type="button" class="btn btn-light" :title="$t('wiki.editor.link')" @click="prompt('link')"><i class="fas fa-link"></i></button>
                <button type="button" class="btn btn-light nch--send-file" :title="$t('wiki.files.hint')" :disabled="uploading" @click="pickFile">
                    <i class="fas mr-1" :class="uploading ? 'fa-circle-notch fa-spin' : 'fa-paperclip'"></i>{{ $t('wiki.files.send') }}
                </button>
            </div>
            <input ref="fileInput" type="file" class="d-none" :accept="fileAccept" @change="onFilePicked" />
        </div>
        <div class="small text-muted mb-2">{{ $t('wiki.files.hint') }}</div>

        <div v-if="promptKind" class="input-group input-group-sm mb-2 nch--prompt">
            <input v-model="promptValue" type="text" class="form-control" :placeholder="$t('wiki.editor.link_url')" @keyup.enter="confirmPrompt" @keyup.esc="promptKind = null" />
            <div class="input-group-append">
                <button type="button" class="btn btn-primary" @click="confirmPrompt">{{ $t('wiki.editor.insert') }}</button>
                <button type="button" class="btn btn-outline-secondary" @click="promptKind = null">{{ $t('wiki.cancel') }}</button>
            </div>
        </div>

        <!-- arquivos enviados nesta edição -->
        <div v-if="uploads.length" class="nch--uploads mb-2">
            <div v-for="u in uploads" :key="u.key" class="nch--upload-card d-flex align-items-center">
                <span class="nch--upload-kind">{{ u.file ? u.file.kind_label : '…' }}</span>
                <div class="flex-grow-1 min-w-0 ml-2">
                    <div class="small font-weight-bold text-truncate">{{ u.name }}</div>
                    <div v-if="u.error" class="small text-danger">{{ u.error }}</div>
                    <div v-else-if="!u.file" class="progress nch--progress"><div class="progress-bar" :style="{ width: `${u.progress}%`, backgroundColor: color.primary }"></div></div>
                    <div v-else class="small text-muted">{{ u.file.size_label }}</div>
                </div>
                <button type="button" class="btn btn-sm btn-link text-danger nch--upload-remove" :disabled="!u.file && !u.error" @click="removeUpload(u)">{{ $t('wiki.files.remove') }}</button>
            </div>
        </div>

        <quill-editor ref="editor" v-model:content="content" content-type="html" :toolbar="false" theme="snow" class="nch--quill" @update:content="onChange"></quill-editor>

        <div class="form-group mt-3 mb-2">
            <label class="font-weight-bold small mb-1">{{ $t('wiki.editor.summary') }}</label>
            <input v-model.trim="summary" type="text" class="form-control nch--summary" maxlength="255" :placeholder="$t('wiki.editor.summary_placeholder')" />
        </div>
        <div class="d-flex justify-content-end">
            <button type="button" class="btn btn-primary nch--save" :style="{ backgroundColor: color.primary, borderColor: color.primary }" :disabled="saving || uploading" @click="save">
                <i class="fas fa-save mr-1"></i>{{ $t('wiki.editor.save') }}
            </button>
        </div>
    </div>
</template>

<script>
import { QuillEditor } from '@vueup/vue-quill';
import '@vueup/vue-quill/dist/vue-quill.snow.css';
import { htmlToText, wordDiff, WIKI_FILE_ACCEPT } from './wikiHelpers';

/**
 * Editor da página da wiki (backlog#343): barra (negrito, itálico, título,
 * lista, tabela, link, enviar arquivo), resumo opcional, marcação "em edição"
 * renovada a cada minuto, aviso de texto não salvo e tratamento de conflito
 * sem perder o rascunho do aluno. Arquivos: imagens entram no texto; PDF,
 * PPTX, DOCX e XLSX entram como link que vira cartão na leitura.
 */
export default {
    name: 'WikiEditor',

    components: { QuillEditor },

    props: {
        wikiId: { type: Number, required: true },
        page: { type: Object, required: true },
    },

    emits: ['saved', 'dirty'],

    data() {
        return {
            content: this.page.raw_content || '',
            initialText: htmlToText(this.page.raw_content || ''),
            summary: '',
            saving: false,
            uploading: false,
            uploads: [],
            baseVersionId: this.page.current_version_id,
            editingBy: this.page.editing_by || null,
            heartbeat: null,
            conflict: null,
            promptKind: null,
            promptValue: '',
            fileAccept: WIKI_FILE_ACCEPT,
        };
    },

    computed: {
        color() { return this.$store.getters.COLOR || {}; },
    },

    mounted() {
        this.mark();
        this.heartbeat = setInterval(this.mark, 60000);
        window.addEventListener('beforeunload', this.release);
    },

    beforeUnmount() {
        clearInterval(this.heartbeat);
        window.removeEventListener('beforeunload', this.release);
        this.release();
    },

    methods: {
        quill() { return this.$refs.editor && this.$refs.editor.getQuill(); },

        /** Marca sujo quando o texto difere do carregado. */
        onChange() {
            this.$emit('dirty', htmlToText(this.content) !== this.initialText || this.uploads.some((u) => u.file));
        },

        mark() {
            window.axios.post(`/api/app/wikis/${this.wikiId}/pages/${this.page.id}/editing`)
                .then(({ data }) => { this.editingBy = data.ok ? null : data.editing_by; })
                .catch(() => {});
        },

        release() {
            try { window.axios.delete(`/api/app/wikis/${this.wikiId}/pages/${this.page.id}/editing`).catch(() => {}); } catch (e) { /* página fechando */ }
        },

        format(name, value = true) {
            const q = this.quill();
            if (!q) return;
            const current = q.getFormat();
            q.format(name, current[name] === value || (value === true && current[name]) ? false : value, 'user');
        },

        heading() {
            const q = this.quill();
            if (!q) return;
            q.format('header', q.getFormat().header === 2 ? false : 2, 'user');
        },

        insertTable() {
            const q = this.quill();
            if (!q) return;
            const range = q.getSelection(true);
            const html = '<table><tbody><tr><td>&nbsp;</td><td>&nbsp;</td></tr><tr><td>&nbsp;</td><td>&nbsp;</td></tr></tbody></table><p><br></p>';
            q.clipboard.dangerouslyPasteHTML(range ? range.index : q.getLength(), html, 'user');
        },

        prompt(kind) {
            this.promptKind = kind;
            this.promptValue = '';
            this.$nextTick(() => { const el = this.$el.querySelector('.nch--prompt input'); if (el) el.focus(); });
        },

        confirmPrompt() {
            const q = this.quill();
            const value = this.promptValue.trim();
            if (!q || !value) { this.promptKind = null; return; }
            const range = q.getSelection(true) || { index: q.getLength(), length: 0 };
            if (range.length) q.format('link', value, 'user'); else q.insertText(range.index, value, { link: value }, 'user');
            this.promptKind = null;
        },

        pickFile() {
            if (this.$refs.fileInput) { this.$refs.fileInput.value = ''; this.$refs.fileInput.click(); }
        },

        /** Envia o arquivo com progresso e insere imagem ou link no texto. */
        async onFilePicked(e) {
            const file = e.target.files && e.target.files[0];
            if (!file) return;
            const upload = { key: `${Date.now()}-${file.name}`, name: file.name, progress: 0, file: null, error: null };
            this.uploads.push(upload);
            this.uploading = true;
            const form = new FormData();
            form.append('file', file);
            form.append('page_id', this.page.id);
            try {
                const { data } = await window.axios.post(`/api/app/wikis/${this.wikiId}/files`, form, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                    onUploadProgress: (ev) => { if (ev.total) upload.progress = Math.round((ev.loaded / ev.total) * 100); },
                });
                upload.file = data.file;
                this.insertFile(data.file);
                this.$notify({ type: 'success', message: this.$t('wiki.files.sent') });
            } catch (err) {
                const res = (err && err.response) || {};
                upload.error = res.status === 422 ? this.$t('wiki.files.invalid') : ((res.data && (res.data.error || res.data.message)) || this.$t('wiki.error'));
            } finally {
                this.uploading = false;
                this.onChange();
            }
        },

        insertFile(file) {
            const q = this.quill();
            if (!q) return;
            const range = q.getSelection(true) || { index: q.getLength(), length: 0 };
            if (file.kind === 'image') {
                q.insertEmbed(range.index, 'image', file.url, 'user');
                q.setSelection(range.index + 1, 0, 'silent');
            } else {
                q.insertText(range.index, file.name, { link: file.url }, 'user');
                q.insertText(range.index + file.name.length, '\n', 'user');
            }
        },

        /** Remove o arquivo (servidor) e tira a imagem/link do texto. */
        async removeUpload(u) {
            if (u.file) {
                await window.axios.delete(`/api/app/wikis/${this.wikiId}/files/${u.file.id}`).catch(() => null);
                const q = this.quill();
                if (q) {
                    const root = q.root;
                    root.querySelectorAll(`img[src="${u.file.url}"], a[href="${u.file.url}"]`).forEach((el) => el.remove());
                    this.content = root.innerHTML;
                }
                this.$notify({ type: 'success', message: this.$t('wiki.files.removed') });
            }
            this.uploads = this.uploads.filter((x) => x !== u);
            this.onChange();
        },

        async save() {
            this.saving = true;
            try {
                const { data } = await window.axios.put(`/api/app/wikis/${this.wikiId}/pages/${this.page.id}`, { content: this.content, summary: this.summary, base_version_id: this.baseVersionId });
                this.conflict = null;
                this.$emit('dirty', false);
                this.$notify({ type: 'success', message: this.$t('wiki.saved') });
                this.$emit('saved', data.page);
            } catch (e) {
                const res = (e && e.response) || {};
                if (res.status === 409 && res.data && res.data.conflict) {
                    const current = res.data.current || {};
                    this.conflict = { ...current, ops: wordDiff(htmlToText(current.content), htmlToText(this.content)) };
                } else {
                    this.$notify({ type: 'danger', message: (res.data && (res.data.error || res.data.message)) || this.$t('wiki.error') });
                }
            } finally {
                this.saving = false;
            }
        },

        /** Mantém o rascunho e passa a salvar por cima da versão atual. */
        keepMine() {
            this.baseVersionId = this.conflict.version_id;
            this.conflict = null;
        },

        /** Descarta o rascunho e carrega a versão atual. */
        useCurrent() {
            this.content = this.conflict.content || '';
            this.baseVersionId = this.conflict.version_id;
            const q = this.quill();
            if (q) q.clipboard.dangerouslyPasteHTML(this.content, 'api');
            this.conflict = null;
            this.initialText = htmlToText(this.content);
            this.$emit('dirty', false);
        },
    },
};
</script>

<style>
.nch--quill .ql-container { min-height: 260px; font-size: 15px; border-radius: 0 0 6px 6px; }
.nch--quill .ql-toolbar { display: none; }
.nch--quill .ql-container.ql-snow { border-top: 1px solid #ccc; border-radius: 6px; }
.nch--wiki-editor .nch--diff-cols { display: flex; gap: 10px; }
.nch--wiki-editor .nch--diff-col { flex: 1; min-width: 0; }
.nch--wiki-editor .nch--diff-pre { white-space: pre-wrap; word-break: break-word; font-family: inherit; font-size: 13px; background: #fff; border: 1px solid #f1b0b0; border-radius: 6px; padding: 8px; max-height: 260px; overflow: auto; }
.nch--diff-del { background: #fde2e1; color: #9f1d1d; text-decoration: line-through; }
.nch--diff-ins { background: #dcf5e3; color: #146c2e; text-decoration: none; }
.nch--upload-card { border: 1px solid #e0e4ea; border-radius: 10px; background: #f8f9fc; padding: 8px 12px; margin-bottom: 6px; }
.nch--upload-kind { flex: 0 0 auto; min-width: 44px; padding: 5px 8px; border-radius: 8px; background: #5e72e4; color: #fff; font-size: 11px; font-weight: 700; text-align: center; }
.nch--progress { height: 6px; margin-top: 4px; }
.min-w-0 { min-width: 0; }
@media (max-width: 800px) { .nch--wiki-editor .nch--diff-cols { flex-direction: column; } }
</style>
